import { messageErreur } from '@imp/shared/message-erreur';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Ban, Send } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { supabase } from '@/lib/supabase';
import { listMessages, markConversationRead, sendSms, type SmsMessage } from '@/modules/messagerie/api';

function heure(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

const STATUT_LABEL: Record<SmsMessage['statut'], string> = {
  en_file: 'en cours…',
  envoye: 'envoyé',
  delivre: 'délivré',
  echec: 'échec',
  recu: 'reçu',
};

interface Props {
  conversationId: string | null;
  telephone: string;
  contactId?: string | null;
  optOut?: boolean;
  onSent?: () => void;
}

export function SmsThread({ conversationId, telephone, contactId, optOut, onSent }: Props) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState('');
  const bottomRef = useRef<HTMLDivElement>(null);

  const messagesQ = useQuery({
    queryKey: ['sms-messages', conversationId],
    queryFn: () => (conversationId ? listMessages(conversationId) : Promise.resolve([])),
    enabled: Boolean(conversationId),
  });

  // Marque comme lu à l'ouverture.
  useEffect(() => {
    if (!conversationId) return;
    void markConversationRead(conversationId).then(() => {
      void queryClient.invalidateQueries({ queryKey: ['sms-conversations'] });
    });
  }, [conversationId, queryClient]);

  // Temps réel : nouveaux messages entrants/sortants.
  useEffect(() => {
    if (!conversationId) return;
    const ch = supabase
      .channel(`sms-${conversationId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'sms_messages', filter: `conversation_id=eq.${conversationId}` },
        () => {
          void queryClient.invalidateQueries({ queryKey: ['sms-messages', conversationId] });
          void queryClient.invalidateQueries({ queryKey: ['sms-conversations'] });
        },
      )
      .subscribe();
    return () => {
      void supabase.removeChannel(ch);
    };
  }, [conversationId, queryClient]);

  const messages = messagesQ.data ?? [];
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  const send = useMutation({
    mutationFn: async (corps: string) => {
      const res = await sendSms({ telephone, corps, contact_id: contactId ?? null });
      if (!res.ok) {
        if (res.skipped === 'opt_out') throw new Error('Contact désinscrit (STOP) : envoi bloqué.');
        throw new Error(res.error ?? res.skipped ?? "L'envoi a échoué.");
      }
      return res;
    },
    onSuccess: () => {
      setDraft('');
      void queryClient.invalidateQueries({ queryKey: ['sms-messages', conversationId] });
      void queryClient.invalidateQueries({ queryKey: ['sms-conversations'] });
      onSent?.();
    },
  });

  const canSend = telephone.trim() !== '' && draft.trim() !== '' && !optOut && !send.isPending;

  return (
    <div className="flex h-full flex-col">
      {/* Fil */}
      <div className="flex-1 space-y-3 overflow-y-auto px-1 py-2">
        {!conversationId ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            Aucun échange pour le moment. Écrivez le premier SMS ci-dessous.
          </p>
        ) : messagesQ.isLoading ? (
          <p className="py-8 text-center text-sm text-muted-foreground">Chargement…</p>
        ) : messages.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">Aucun message.</p>
        ) : (
          messages.map((m) => {
            const out = m.direction === 'sortant';
            return (
              <div key={m.id} className={`flex ${out ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-sm ${
                    out
                      ? 'rounded-br-sm bg-primary text-primary-foreground'
                      : 'rounded-bl-sm bg-muted text-foreground'
                  }`}
                >
                  <p className="whitespace-pre-wrap break-words">{m.corps}</p>
                  <p className={`mt-1 text-[10px] ${out ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>
                    {heure(m.created_at)}
                    {out ? ` · ${STATUT_LABEL[m.statut]}` : ''}
                    {m.est_automatique ? ' · auto' : ''}
                    {m.statut === 'echec' && m.erreur ? ` · ${m.erreur.slice(0, 60)}` : ''}
                  </p>
                </div>
              </div>
            );
          })
        )}
        <div ref={bottomRef} />
      </div>

      {/* Bannière opt-out */}
      {optOut ? (
        <div className="flex items-center gap-2 border-t border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive">
          <Ban className="h-4 w-4 shrink-0" />
          Ce contact s&apos;est désinscrit (STOP). L&apos;envoi de SMS est bloqué.
        </div>
      ) : null}

      {/* Rédaction */}
      <div className="border-t pt-3">
        {send.isError ? (
          <div className="mb-2 flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            {messageErreur(send.error)}
          </div>
        ) : null}
        <div className="flex items-end gap-2">
          <Textarea
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value);
            }}
            placeholder={optOut ? 'Envoi désactivé (désinscription)' : 'Votre message…'}
            disabled={Boolean(optOut) || send.isPending}
            rows={2}
            className="min-h-[44px] flex-1 resize-none"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && canSend) {
                e.preventDefault();
                send.mutate(draft.trim());
              }
            }}
          />
          <Button
            type="button"
            disabled={!canSend}
            onClick={() => {
              send.mutate(draft.trim());
            }}
            className="h-11"
          >
            <Send className="h-4 w-4" />
            {send.isPending ? 'Envoi…' : 'Envoyer'}
          </Button>
        </div>
        <p className="mt-1 text-[11px] text-muted-foreground">
          {draft.length} caractère{draft.length > 1 ? 's' : ''} · {Math.max(1, Math.ceil(draft.length / 153))} SMS ·
          Ctrl+Entrée pour envoyer
        </p>
      </div>
    </div>
  );
}
